import t from 'react-translate';
import moment from 'moment';
import { css } from '@emotion/react';
import ClickableContainer from 'components/clickable-container';
import NvIcon from 'shared/components/nv-icon';
import { gray6 } from 'styles/global_defaults/colors';
import {
  halfSpacing,
  quarterSpacing,
} from 'styles/global_defaults/scaffolding';
import { MentoringProgramActionItem } from 'redux/schemas/models/mentoring-program-action-items';
import { MentoringProgramEnrollment } from 'redux/schemas/models/mentoring-program-enrollments';
import { MentorshipProgramSession } from 'redux/schemas/models/mentoring-program-sessions';
import SwitchAssigneeOption from 'athena/components/switch-assignee-option';
import SessionMenuOption from './session-menu-option';
import { optionStyles } from './styles';
import DeleteActionOption from './delete-action-option';
import { config } from '@config/pendo.config.json';

type ActionMenuProps = {
  item: MentoringProgramActionItem;
  disabled: boolean;
  isCompleted: boolean;
  isEditing: boolean;
  isReadOnly?: boolean;
  assignedTo: number;
  sessionId: number;
  enrollments?: { [id: number]: MentoringProgramEnrollment };
  onChange: () => void;
  onDelete: () => void;
  showPlanSessionFlyout?: (session: MentorshipProgramSession) => void;
};

const editStyles = css`
  ${optionStyles}
  margin: 1px;
  padding: ${quarterSpacing}px;

  &.disabled {
    pointer-events: none;
    background: ${gray6} !important;
  }
`;

const menuStyles = css`
  gap: ${halfSpacing}px;
`;

const ActionMenu = ({
  item,
  disabled,
  isCompleted,
  isEditing,
  isReadOnly = false,
  assignedTo,
  sessionId,
  enrollments,
  onChange,
  onDelete,
  showPlanSessionFlyout,
}: ActionMenuProps) => {
  const { id: itemId, completedAt, completedBy } = item;
  const dataQATagBase = config.pendo.athena.mentorshipProgram.participantHome.actionItems.item;
  const dataQATag = itemId ? dataQATagBase.updateActionItemCheckButton : dataQATagBase.createNewActionItemAddButton;
  const translationBase = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.ACTION_ITEMS.ARIA_LABEL;
  const ariaLabel = itemId ? translationBase.UPDATE_ACTION_ITEM() : translationBase.ADD_ACTION_ITEM();
  const actionItemTranslations = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.ACTION_ITEMS.ITEM.TOOLTIP;
  const completedTooltip = isCompleted && completedBy && completedAt
    ? actionItemTranslations.COMPLETED_BY(moment(completedAt).format(actionItemTranslations.DATE_FORMAT()), completedBy.user.fullName)
    : undefined;
  const switchAssigneeDataQATag = dataQATagBase.editActionItemAssignedTo;

  if (isReadOnly) {
    if (!item.assignedTo) return null;
    return (
      <SwitchAssigneeOption
        itemId={itemId}
        assignedTo={item.assignedTo.id}
        enrollments={{ [item.assignedTo.id]: item.assignedTo as MentoringProgramEnrollment }}
        dataQATag={switchAssigneeDataQATag}
        completedTooltip={completedTooltip}
        isReadOnly
      />
    );
  }

  if (isEditing) {
    return (
      <ClickableContainer
        disabled={disabled}
        css={editStyles}
        className={disabled ? 'disabled' : ''}
        onClick={onChange}
        data-qa={dataQATag}
        data-qa-id={`${dataQATag}-${itemId}`}
        aria-label={ariaLabel}
      >
        <NvIcon icon={itemId ? 'check' : 'create-new-post'} size='smallest' />
      </ClickableContainer>
    );
  }

  if (itemId) {
    return (
      <div css={menuStyles} className='d-flex align-items-center'>
        {sessionId && (
          <SessionMenuOption
            actionItemId={itemId}
            sessionId={sessionId}
            showPlanSessionFlyout={showPlanSessionFlyout}
          />
        )}
        <SwitchAssigneeOption
          itemId={itemId}
          assignedTo={assignedTo}
          enrollments={enrollments}
          dataQATag={switchAssigneeDataQATag}
          completedTooltip={completedTooltip}
        />
        <DeleteActionOption actionItemId={itemId} onDelete={onDelete} />
      </div>
    );
  }
  return null;
};

export default ActionMenu;
